import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import fnolStateService from '../../services/fnolStateService';
import './FraudAnalysisPage.css';

const FraudAnalysisPage = ({ selected, analysisData, loadingAnalysis, isCompleted, setIsCompleted, onPrevious, onNext, hasClaimId }) => {
  const navigate = useNavigate();

  const [expandedCategories, setExpandedCategories] = useState({});
  const [comment, setComment] = useState('');
  const commentTextareaRef = React.useRef(null);

  const handleComplete = async () => {
    if (!selected?.id) return;
    // Prevent status changes if claim ID exists
    if (hasClaimId) {
      console.log('⚠️ Cannot change completion status - claim already exists');
      return;
    }
    const newState = !isCompleted;
    setIsCompleted(newState);

    // Update API state
    try {
      await fnolStateService.updateState(selected.id, {
        FraudAnalysis: newState ? 'completed' : 'visited'
      });
      console.log('✅ Fraud Analysis completion state updated in API');
    } catch (error) {
      console.error('Failed to update Fraud Analysis state in API:', error);
    }

  };

  // Transform API fraud analysis data to UI format
  const transformedAnalysisData = useMemo(() => {
    if (!analysisData) {
      return {
        company: selected?.company || 'N/A',
        fnolId: selected?.id || 'FNOL-?',
        summary: 'Fraud Analysis data not available.',
        overallProgress: 0,
        categories: []
      };
    }

    // Transform checks array from API to categories for UI
    const categories = analysisData.checks?.map((check, index) => {
      // Parse details to create checks array
      const checks = [];
      if (check.details) {
        Object.entries(check.details).forEach(([key, detail]) => {
          checks.push({
            text: key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
            status: detail.value === 'Yes' || detail.value === true,
            value: detail.reason || detail.value
          });
        });
      }

      return {
        id: index + 1,
        title: check.check_name,
        accuracy: check.score,
        checks: checks
      };
    }) || [];

    return {
      company: selected?.company || 'N/A',
      fnolId: analysisData.fnol_id || selected?.id || 'FNOL-?',
      summary: analysisData.ai_summary || 'Fraud Analysis summary not available.',
      overallProgress: analysisData.overall_score || 0,
      categories: categories
    };
  }, [analysisData, selected]);

  const getProgressBarColor = (accuracy) => {
    if (accuracy === 100) return '#8CE27D'; // Green
    if (accuracy >= 50) return '#FFD996'; // Yellow
    return '#FFA6A6'; // Red
  };

  const handleContinue = () => {
    console.log('Continue button clicked - navigating to /fraud-rules');
    navigate('/fraud-rules'); // Navigate to next page
  };

  // Comment formatting functions
  const applyFormatting = (prefix, suffix = '') => {
    const textarea = commentTextareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = comment.substring(start, end);
    const beforeText = comment.substring(0, start);
    const afterText = comment.substring(end);

    const newText = beforeText + prefix + selectedText + suffix + afterText;
    setComment(newText);

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + prefix.length + selectedText.length + suffix.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleBold = () => applyFormatting('**', '**');
  const handleItalic = () => applyFormatting('*', '*');
  const handleUnderline = () => applyFormatting('<u>', '</u>');
  const handleStrikethrough = () => applyFormatting('~~', '~~');
  const handleBulletList = () => {
    const textarea = commentTextareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const beforeText = comment.substring(0, start);
    const afterText = comment.substring(start);
    const newText = beforeText + '\n• ';
    setComment(newText + afterText);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newText.length, newText.length);
    }, 0);
  };
  const handleNumberedList = () => {
    const textarea = commentTextareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const beforeText = comment.substring(0, start);
    const afterText = comment.substring(start);
    const newText = beforeText + '\n1. ';
    setComment(newText + afterText);
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newText.length, newText.length);
    }, 0);
  };


  return (
    <div
      className="h-100 overflow-auto auto-hide-scrollbar"
    >
      <div className="card border-0 bg-transparent">
        <div className="card-body p-0">

          {/* Summary Section */}
          <section className="card mb-2" style={{ borderRadius: '10px' }}>
            <div className="card-body">
              <h6 className="card-title mb-3 fw-bold">Summary</h6>
              {loadingAnalysis ? (
                <div className="text-center py-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading fraud analysis data...</span>
                  </div>
                  <p className="mt-2 text-muted">Loading fraud analysis data...</p>
                </div>
              ) : (
                <>
                  <div className="mb-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="progress flex-grow-1 me-3" style={{ height: '16px' }}>
                        <div
                          className="progress-bar"
                          role="progressbar"
                          style={{
                            width: `${transformedAnalysisData.overallProgress}%`,
                            backgroundColor: getProgressBarColor(transformedAnalysisData.overallProgress)
                          }}
                          aria-valuenow={transformedAnalysisData.overallProgress}
                          aria-valuemin="0"
                          aria-valuemax="100"
                        />
                      </div>
                      <span className="fw-bold">{transformedAnalysisData.overallProgress.toFixed(2)}%</span>
                    </div>
                  </div>
                  <div className=" border p-2.5 rounded"><p className="text-muted small mb-0">{transformedAnalysisData.summary}</p></div>
                </>
              )}
            </div>
          </section>

          {/* Fraud Analysis Check List */}
          <section className="card mb-2 p-3" style={{ borderRadius: '10px' }}>
            <h5 className="mb-4 fw-bold">Fraud Analysis Check List</h5>
            {loadingAnalysis ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : transformedAnalysisData.categories.length === 0 ? (
              <div className="text-center py-4 text-muted">
                <p>No fraud analysis data available</p>
              </div>
            ) : (
              <div className="card mb-2 fraud-analysis-checklist" style={{ borderRadius: '10px', overflow: 'hidden', border: 'none' }}>
                <div className="card-body p-0">
                  {/* Header Row */}
                  <div className="row g-0 py-2 px-3" style={{ backgroundColor: '#d4e6f1', borderBottom: '1px solid #dee2e6' }}>
                    <div className="col-4">
                      <strong style={{ fontSize: '0.9rem' }}>Checklist</strong>
                    </div>
                    <div className="col-3">
                      <strong style={{ fontSize: '0.9rem' }}>True Value</strong>
                    </div>
                    <div className="col-5">
                      <strong style={{ fontSize: '0.9rem' }}>Overall Accuracy for Check List</strong>
                    </div>
                  </div>

                  {/* Category Rows */}
                  {transformedAnalysisData.categories.map((category, catIdx) => (
                    <div
                      key={category.id}
                      className="row g-0 py-3 px-3"
                      style={{
                        backgroundColor: catIdx % 2 === 0 ? '#f8f9fa' : 'white',
                        borderBottom: catIdx < transformedAnalysisData.categories.length - 1 ? '1px solid #dee2e6' : 'none'
                      }}
                    >
                      {/* Checklist Column */}
                      <div className="col-4">
                        <div className="mb-2">
                          <strong style={{ fontSize: '0.85rem' }}>{category.id}. {category.title}</strong>
                        </div>
                        <div>
                          {category.checks.map((check, idx) => (
                            <div key={idx} className="d-flex align-items-start mb-1" style={{ fontSize: '0.8rem' }}>
                              <span className={`me-2 ${check.status ? 'text-success' : 'text-danger'}`} style={{ fontSize: '1rem', lineHeight: '1.2' }}>
                                {check.status ? '✓' : '✗'}
                              </span>
                              <span style={{ lineHeight: '1.4' }}>{check.text}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* True Value Column */}
                      <div className="col-3">
                        <div className="mb-2" style={{ height: '22px' }}></div>
                        <div>
                          {category.checks.map((check, idx) => (
                            <div key={idx} className="mb-1" style={{ fontSize: '0.8rem', minHeight: '22px', lineHeight: '1.4' }}>
                              {check.value}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Overall Accuracy Column */}
                      <div className="col-5">
                        {/* Progress bar on same line as title */}
                        <div className="d-flex align-items-center mb-2" style={{ height: '22px' }}>
                          <div className="flex-grow-1 me-3" style={{ maxWidth: '321px' }}>
                            <div className="d-flex align-items-center">
                              <span className="fw-bold me-2" style={{ fontSize: '0.9rem', minWidth: '40px' }}>
                                {category.accuracy}%
                              </span>
                              <div className="progress flex-grow-1" style={{ height: '10px', backgroundColor: '#e0e0e0' }}>
                                <div
                                  className="progress-bar"
                                  role="progressbar"
                                  style={{
                                    width: `${category.accuracy}%`,
                                    backgroundColor: getProgressBarColor(category.accuracy)
                                  }}
                                  aria-valuenow={category.accuracy}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                />
                              </div>
                            </div>
                          </div>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            style={{
                              width: '32px',
                              height: '32px',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                            title="View details"
                          >
                            <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                              <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2zm2-1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H4z" />
                              <path d="M6 5.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5zm0 2a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Comments Section */}
          <section className="card mb-2" style={{ borderRadius: '10px' }}>
            <div className="card-body">
              {/* Label and Toolbar Row */}
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <label className="form-label fw-bold mb-0">
                  Comments
                </label>
                {/* Toolbar */}
                <div className="d-flex gap-1 align-items-center flex-wrap">
                  <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Bold" onClick={handleBold} style={{ width: '1.75rem', height: '1.75rem' }}>
                    <strong style={{ fontSize: '0.85rem' }}>B</strong>
                  </button>
                  <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Italic" onClick={handleItalic} style={{ width: '1.75rem', height: '1.75rem' }}>
                    <em style={{ fontSize: '0.85rem' }}>I</em>
                  </button>
                  <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Underline" onClick={handleUnderline} style={{ width: '1.75rem', height: '1.75rem' }}>
                    <u style={{ fontSize: '0.85rem' }}>U</u>
                  </button>
                  <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Strikethrough" onClick={handleStrikethrough} style={{ width: '1.75rem', height: '1.75rem' }}>
                    <s style={{ fontSize: '0.85rem' }}>S</s>
                  </button>
                  <div className="vr" style={{ height: '1.25rem' }}></div>
                  <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Bullet List" onClick={handleBulletList} style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                    ≡
                  </button>
                  <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Numbered List" onClick={handleNumberedList} style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                    ≣
                  </button>
                  <div className="vr" style={{ height: '1.25rem' }}></div>
                  <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Link" style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                    🔗
                  </button>
                  <button type="button" className="btn btn-sm btn-light border-0 p-1" title="Image" style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                    🖼
                  </button>
                  <button type="button" className="btn btn-sm btn-light border-0 p-1" title="More" style={{ width: '1.75rem', height: '1.75rem', fontSize: '0.85rem' }}>
                    ⋯
                  </button>
                </div>
              </div>

              {/* Text Area */}
              <textarea
                ref={commentTextareaRef}
                className="form-control"
                rows="6"
                placeholder=""
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                style={{
                  resize: 'none',
                  borderRadius: '6px',
                  borderColor: '#ced4da'
                }}
              ></textarea>
            </div>
          </section>

          {/* Action Buttons */}
          <section className="card" style={{ borderRadius: '10px' }}>
            <div className="card-body">
              <div className="d-flex justify-content-between gap-2 action-buttons-container">
                <button
                  type="button"
                  className="btn fraud-action-btn fraud-btn-outline"
                  onClick={onPrevious}
                >
                  Previous
                </button>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className={`btn fraud-action-btn ${isCompleted ? 'btn-success' : 'fraud-btn-primary'}`}
                    style={{
                      backgroundColor: isCompleted ? '#006BB3' : undefined,
                      borderColor: '#006BB3',
                      color: isCompleted ? 'white' : undefined
                    }}
                    onClick={handleComplete}
                    disabled={hasClaimId}
                  >
                    {isCompleted ? (
                      <>
                        <span style={{ marginRight: '6px' }}>✓</span>
                        Completed
                      </>
                    ) : (
                      'Complete'
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn fraud-action-btn fraud-btn-primary"
                    onClick={onNext}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default FraudAnalysisPage;
